import { computed, type ComputedRef } from 'vue';
import dayjs from 'dayjs';
import { useLocale as useVuetifyLocale } from 'vuetify';

import { i18n } from '../plugins';
import { createSingleton } from '../utils';

const innerCultureInfo: Record<string, { hourCycle: 'h12' | 'h23'; firstDayOfWeek: number, firstDayOfYear: number }> = {
    'de-DE': {
        hourCycle: 'h23',
        firstDayOfWeek: 1,
        firstDayOfYear: 4
    },
    'en-US': {
        hourCycle: 'h12',
        firstDayOfWeek: 0,
        firstDayOfYear: 1
    },
    'fr-FR': {
        hourCycle: 'h23',
        firstDayOfWeek: 1,
        firstDayOfYear: 4
    }
};

export const useLocale = createSingleton((): {
    availableLocales: ComputedRef<string[]>;
    locale: ComputedRef<string>;
    cultureInfo: ComputedRef<{ hourCycle: 'h12' | 'h23'; firstDayOfWeek: number, firstDayOfYear: number }>;
    setLocale: (language: string) => Promise<void>;
    getLocalizedHours: () => string[];
    getLocalizedWeekdays: () => string[];
    t: (key: string, ...args: any[]) => string;
} => {
    const { current: vuetifyLocale } = useVuetifyLocale();

    const availableLocales = computed((): string[] => (i18n.value as any).global.availableLocales);

    const locale = computed((): string => (i18n.value as any).global.locale);

    const cultureInfo = computed((): { hourCycle: 'h12' | 'h23'; firstDayOfWeek: number, firstDayOfYear: number } => {
        return innerCultureInfo[locale.value] as { hourCycle: 'h12' | 'h23'; firstDayOfWeek: number, firstDayOfYear: number };
    });

    const t = (key: string, ...args: any[]): string => (i18n.value as any).global.t(key, ...args);

    const setLocale = async (language: string): Promise<void> => {
        if ((i18n.value as any).global.availableLocales.includes(language)) {
            (i18n.value as any).global.locale = language;
            vuetifyLocale.value = language;
            dayjs.locale(language.split('-')[0]);
        }
    };

    const getLocalizedHours = (): string[] => {
        return Array.from({ length: 24 }, (_, hour) => new Intl.DateTimeFormat((i18n.value as any).global.locale, {
            hour: 'numeric',
            hourCycle: cultureInfo.value.hourCycle
        }).format(new Date(0, 0, 0, hour, 0, 0)));
    };

    const getLocalizedWeekdays = (): string[] => {
        return [
            (i18n.value as any).global.t('shared.utils.dates.monday'),
            (i18n.value as any).global.t('shared.utils.dates.tuesday'),
            (i18n.value as any).global.t('shared.utils.dates.wednesday'),
            (i18n.value as any).global.t('shared.utils.dates.thursday'),
            (i18n.value as any).global.t('shared.utils.dates.friday'),
            (i18n.value as any).global.t('shared.utils.dates.saturday'),
            (i18n.value as any).global.t('shared.utils.dates.sunday')
        ];
    };

    return {
        availableLocales,
        locale,
        cultureInfo,
        setLocale,
        getLocalizedHours,
        getLocalizedWeekdays,
        t
    };
});